// src/components/tokenPage/overview/SectorHeatCard.tsx

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchTokensChanges } from "@/services/queries/tokens/tokensScreenerQueries";
import { useTokenFundamentals } from "@/contexts/token";
import { DataCard } from "@/components/ui";

interface Props {
  ticker: string;
  sectorName: string | null;
}

/**
 * SectorHeatCard - Shows valuation vs growth quadrant
 * 
 * Uses TokenFundamentalsContext for MC/Fees ratio.
 * Still queries for growth data (changes) as this isn't in the context.
 */
const SectorHeatCard: React.FC<Props> = ({ ticker, sectorName }) => {
  // Get MC/Fees from context
  const { current, isLoading: fundamentalsLoading } = useTokenFundamentals();
  const mcFees = current.mcToFees;

  // Fetch growth data (not in context)
  const changesQ = useQuery({
    queryKey: ["sector-heat-changes", ticker],
    queryFn: async ({ signal }) => 
      fetchTokensChanges({ tickersCsv: ticker.toUpperCase(), metricKeysCsv: "fees,dau", windowsCsv: "365d", signal }),
    staleTime: 60_000,
    enabled: !!ticker,
  });

  const growthFees1y = useMemo(() => {
    const t = changesQ.data?.tokens?.[0];
    const m = t?.metrics?.find(m => m.metric_key === "fees");
    const v = m?.windows?.["365d"] as { value?: number | null } | undefined;
    return v?.value != null ? Number(v.value) * 100 : null;
  }, [changesQ.data]);

  const growthUsers1y = useMemo(() => {
    const t = changesQ.data?.tokens?.[0];
    const m = t?.metrics?.find(m => m.metric_key === "dau");
    const v = m?.windows?.["365d"] as { value?: number | null } | undefined;
    return v?.value != null ? Number(v.value) * 100 : null;
  }, [changesQ.data]);

  const quadrant = useMemo(() => {
    const val = Number(mcFees ?? NaN);
    const grow = Number((growthFees1y ?? growthUsers1y) ?? NaN);
    if (!Number.isFinite(val) || !Number.isFinite(grow)) return "Unknown";
    const valGood = val > 0 ? val < 20 : false; // heuristic threshold
    const growGood = grow > 0; // positive YoY
    if (valGood && growGood) return "High Growth / Attractive Valuation";
    if (!valGood && growGood) return "High Growth / Expensive";
    if (valGood && !growGood) return "Low/Neg Growth / Attractive";
    return "Low/Neg Growth / Expensive";
  }, [mcFees, growthFees1y, growthUsers1y]);

  const isLoading = fundamentalsLoading || changesQ.isLoading;

  // Hide if no data available (after loading)
  const hasData = mcFees != null || growthFees1y != null || growthUsers1y != null;
  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <DataCard
      title="Sector Heat"
      subtitle={`Valuation vs Growth${sectorName ? ` • ${sectorName}` : ''}`}
      isLoading={isLoading}
      loadingVariant="stats"
    >
      <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-300">
        <div className={`rounded p-2 ${quadrant.includes("High Growth") && quadrant.includes("Attractive") ? 'bg-emerald-500/10' : 'bg-gray-700/40'}`}>High Growth / Attractive</div>
        <div className={`rounded p-2 ${quadrant.includes("High Growth") && quadrant.includes("Expensive") ? 'bg-yellow-500/10' : 'bg-gray-700/40'}`}>High Growth / Expensive</div>
        <div className={`rounded p-2 ${quadrant.includes("Low/Neg Growth") && quadrant.includes("Attractive") ? 'bg-blue-500/10' : 'bg-gray-700/40'}`}>Low Growth / Attractive</div>
        <div className={`rounded p-2 ${quadrant.includes("Low/Neg Growth") && quadrant.includes("Expensive") ? 'bg-rose-500/10' : 'bg-gray-700/40'}`}>Low Growth / Expensive</div>
      </div>
      <div className="mt-2 text-xs text-slate-400">MC/Fees: {mcFees == null ? 'N/A' : Number(mcFees).toFixed(1)} • Fees YoY: {growthFees1y == null ? 'N/A' : `${growthFees1y.toFixed(1)}%`}</div>
    </DataCard>
  );
};

export default SectorHeatCard;
